import React, { useEffect, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Link,
    Paper,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "axios";
import axiosInstance from "../AxiosInstance";

interface ApiKeySummary {
    organisationApiKeyId: string;
    name: string;
    keyPrefix: string;
    scope: string;
    expiresAtUtc: string | null;
    revokedAtUtc: string | null;
    lastUsedAtUtc: string | null;
    createdUtc: string;
    isActive: boolean;
}

interface CreatedApiKey extends ApiKeySummary {
    secret: string;
}

function formatWhen(value: string | null) {
    if (!value) {
        return "—";
    }

    return new Date(value).toLocaleString();
}

export default function ApiKeysSettingsPage() {
    const [keys, setKeys] = useState<ApiKeySummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [name, setName] = useState("");
    const [creating, setCreating] = useState(false);
    const [created, setCreated] = useState<CreatedApiKey | null>(null);
    const [revokeId, setRevokeId] = useState<string | null>(null);
    const [openingDocs, setOpeningDocs] = useState(false);
    const createAttempt = useRef<{ name: string; idempotencyKey: string } | null>(null);
    const loadAbort = useRef<AbortController | null>(null);

    const load = async () => {
        loadAbort.current?.abort();
        const controller = new AbortController();
        loadAbort.current = controller;
        const signal = controller.signal;

        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get<ApiKeySummary[]>("/api-keys", { signal });
            if (signal.aborted) {
                return;
            }

            setKeys(Array.isArray(response.data) ? response.data : []);
        } catch (requestError) {
            if (signal.aborted || axios.isCancel(requestError) || (requestError as { code?: string })?.code === "ERR_CANCELED") {
                return;
            }

            setError("Could not load API keys.");
        } finally {
            if (!signal.aborted) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        void load();
        return () => loadAbort.current?.abort();
    }, []);

    const createKey = async () => {
        const trimmedName = name.trim();
        if (!createAttempt.current || createAttempt.current.name !== trimmedName) {
            createAttempt.current = { name: trimmedName, idempotencyKey: crypto.randomUUID() };
        }

        setCreating(true);
        setError(null);
        try {
            const response = await axiosInstance.post<CreatedApiKey>("/api-keys", {
                name: trimmedName,
                idempotencyKey: createAttempt.current.idempotencyKey
            });
            createAttempt.current = null;
            setCreated(response.data);
            setName("");
            await load();
        } catch (requestError: unknown) {
            const message = axiosMessage(requestError) ?? "Could not create API key.";
            setError(message);
        } finally {
            setCreating(false);
        }
    };

    const revoke = async () => {
        if (!revokeId) {
            return;
        }

        setError(null);
        try {
            await axiosInstance.delete(`/api-keys/${revokeId}`);
            setRevokeId(null);
            await load();
        } catch {
            setError("Could not revoke API key.");
        }
    };

    const openDocs = async () => {
        const docsWindow = window.open("", "_blank");
        if (!docsWindow) {
            setError("Could not open API docs.");
            return;
        }

        setOpeningDocs(true);
        try {
            await axiosInstance.post("/api-keys/docs-session");
            docsWindow.opener = null;
            docsWindow.location.href = "/docs";
        } catch {
            docsWindow.close();
            setError("Could not open API docs.");
        } finally {
            setOpeningDocs(false);
        }
    };

    const activeCount = keys.filter(key => key.isActive).length;

    return (
        <Box className="component-container">
            <Container component="main" maxWidth="md">
                <Paper
                    elevation={3}
                    sx={{ p: 3 }}
                >
                    <Typography variant="h5" gutterBottom>API keys</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Create a key to call the Abakus API. The secret is shown once - copy it somewhere safe.
                        Use it as <code>Authorization: Bearer …</code>.
                        <br />
                        <Link
                            component="button"
                            type="button"
                            disabled={openingDocs}
                            onClick={() => { void openDocs(); }}
                        >
                            Open API docs
                        </Link>.
                    </Typography>

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                        <TextField
                            label="Key name"
                            value={name}
                            onChange={event => setName(event.target.value)}
                            size="small"
                            fullWidth
                            onKeyDown={event => {
                                if (event.key === "Enter" && name.trim().length > 0 && activeCount < 2 && !creating) {
                                    void createKey();
                                }
                            }}
                        />
                        <Button
                            variant="contained"
                            disabled={creating || name.trim().length === 0 || activeCount >= 2}
                            onClick={() => void createKey()}
                            sx={{ flexShrink: 0 }}
                        >
                            Create key
                        </Button>
                    </Stack>

                    {loading ? (
                        <Typography color="text.secondary">Loading…</Typography>
                    ) : keys.length === 0 ? (
                        !error && <Typography color="text.secondary">No API keys yet.</Typography>
                    ) : (
                        <Stack spacing={1.5} divider={<Divider flexItem />}>
                            {keys.map(key => {
                                const revoked = !!key.revokedAtUtc;
                                const status = key.isActive ? "Active" : revoked ? "Revoked" : "Expired";

                                return (
                                    <Stack
                                        key={key.organisationApiKeyId}
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{ py: 0.5 }}
                                    >
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="subtitle1" noWrap>
                                                {key.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Prefix <code>{key.keyPrefix}</code>
                                                {" · "}
                                                {status}
                                                {" · "}
                                                Created {formatWhen(key.createdUtc)}
                                                {key.lastUsedAtUtc ? ` · Last used ${formatWhen(key.lastUsedAtUtc)}` : ""}
                                            </Typography>
                                        </Box>
                                        {!revoked && (
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={() => setRevokeId(key.organisationApiKeyId)}
                                                sx={{ flexShrink: 0 }}
                                            >
                                                Revoke
                                            </Button>
                                        )}
                                    </Stack>
                                );
                            })}
                        </Stack>
                    )}
                </Paper>
            </Container>

            <Dialog open={!!created} onClose={() => setCreated(null)} fullWidth maxWidth="sm">
                <DialogTitle>Copy this secret now</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This is the only time the secret is shown. Store it somewhere safe.
                    </Alert>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField value={created?.secret ?? ""} fullWidth size="small" slotProps={{ input: { readOnly: true } }} />
                        <IconButton
                            aria-label="Copy secret"
                            onClick={() => {
                                if (created?.secret) {
                                    void navigator.clipboard.writeText(created.secret);
                                }
                            }}
                        >
                            <ContentCopyIcon />
                        </IconButton>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreated(null)}>Done</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!revokeId} onClose={() => setRevokeId(null)}>
                <DialogTitle>Revoke API key?</DialogTitle>
                <DialogContent>
                    Requests using this key will fail immediately.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevokeId(null)}>Cancel</Button>
                    <Button color="error" onClick={() => void revoke()}>Revoke</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

function axiosMessage(error: unknown): string | null {
    if (typeof error === "object" && error && "response" in error) {
        const response = (error as { response?: { data?: { message?: string } } }).response;
        return response?.data?.message ?? null;
    }

    return null;
}
